/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useRef } from 'react';
import Button from '@atlaskit/button/default/button';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { Text } from '@atlaskit/primitives/compiled/text';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import ShortcutScope from '@jira/platform__common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';

const deleteMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.delete-modal.delete',
	defaultMessage: 'Delete',
	description: 'Button label for deleting a view section',
});

const cancel = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.delete-modal.cancel',
	defaultMessage: 'Cancel',
	description: 'Button label to cancel the deletion of view or view section',
});

const deleteHeader = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.delete-modal.delete-header',
	defaultMessage: 'Delete "{title}" ?',
	description: 'Header caption in delete view section confirmation dialog',
});

const description = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.delete-modal.description',
	defaultMessage: 'This deletes {title}. This action cannot be undone.',
	description: 'Description in view section confirmation dialog',
});

type Props = {
	isOpen: boolean;
	title: string;
	onCancel: () => void;
	onConfirm: () => void;
};

export const DeleteModal = (props: Props): React.JSX.Element => {
	const { isOpen, title, onCancel, onConfirm } = props;
	const { formatMessage } = useIntl();
	const focusRef = useRef<HTMLButtonElement>(null);

	return (
		<ModalTransition>
			{isOpen && (
				<ShortcutScope>
					<ModalDialog
						// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
						messageId="navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.delete-modal.modal-dialog"
						messageType="transactional"
						autoFocus={focusRef}
						onClose={onCancel}
					>
						<ModalHeader hasCloseButton>
							<ModalTitle appearance="danger">{formatMessage(deleteHeader, { title })}</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<Text>{formatMessage(description, { title })}</Text>
						</ModalBody>
						<ModalFooter>
							<Button
								onClick={onCancel}
								// eslint-disable-next-line jira/integration/test-id-by-folder-structure
								testId="navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.delete-modal.cancel"
							>
								{formatMessage(cancel)}
							</Button>
							<Button
								appearance="danger"
								onClick={onConfirm}
								// eslint-disable-next-line jira/integration/test-id-by-folder-structure
								testId="navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.delete-modal.delete"
								ref={focusRef}
							>
								{formatMessage(deleteMessage)}
							</Button>
						</ModalFooter>
					</ModalDialog>
				</ShortcutScope>
			)}
		</ModalTransition>
	);
};
